import {
  parseQueryString,
  createQueryString
} from 'common/utils/queryStringUtils';
import {
  isDayOnlyDateFormat,
  formatDayOnlyDate,
  DateTime
} from 'common/utils/date';

interface PartialLocation {
  pathname: string;
  search: string;
}

const allowedUrls = [
  '/trends/company/teams',
  '/trends/company/team_members',
  '/trends/company/reporting_groups',
  '/trends/service_recovery',
  '/trends/company',
  '/trends/company/program',
  '/trends/company/multi',
  '/trends/company/tags',
];

const isInTrends = (currentLocation: PartialLocation) =>
  currentLocation.pathname.indexOf('/trends') !== -1;

const isAllowedUrl = (url: string) =>
  allowedUrls.some(allowedUrl => url.indexOf(allowedUrl) !== -1);

const buildFilters = (
  currentLocation: PartialLocation,
  originalUrl: string
) => {
  const urlQueryParams = parseQueryString(currentLocation.search);
  const {
    start_date: startDate,
    end_date: endDate,
    solicitationReportingTimestamp,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    solicitation_reporting_timestamp,
  } = urlQueryParams;

  // We need to make sure that dates are in YYYY-MM-DD format, if they're not,
  // modify them to be in YYYY-MM-DD.
  if (startDate && !isDayOnlyDateFormat(startDate as string)) {
    const isoDate = DateTime.fromFormat(
      startDate as string,
      'MM/dd/yyyy'
    ).toISO();
    urlQueryParams.start_date = formatDayOnlyDate(isoDate);
  }

  if (endDate && !isDayOnlyDateFormat(endDate as string)) {
    const isoDate = DateTime.fromFormat(
      endDate as string,
      'MM/dd/yyyy'
    ).toISO();
    urlQueryParams.end_date = formatDayOnlyDate(isoDate);
  }

  /* We have two different kinds of the date range selectors(legacy and Alchemy)
  and only service recovery page uses the legacy one. Here we are setting the
  proper solicitation timestamp param depending on the page */
  const serviceRecoveryPath = '/trends/service_recovery';
  const path = currentLocation.pathname;

  if (originalUrl === serviceRecoveryPath && solicitationReportingTimestamp) {
    urlQueryParams.solicitation_reporting_timestamp = (
      solicitationReportingTimestamp as string
    ).toLowerCase();

    delete urlQueryParams.solicitationReportingTimestamp;
  }
  if (path === serviceRecoveryPath && solicitation_reporting_timestamp) {
    urlQueryParams.solicitationReportingTimestamp = (
      solicitation_reporting_timestamp as string
    ).toUpperCase();

    delete urlQueryParams.solicitation_reporting_timestamp;
  }

  return createQueryString(urlQueryParams);
};

export const trendsFilterDuplicator = {
  modifyUrl: (currentLocation: PartialLocation, originalUrl: string) => {
    if (!isInTrends(currentLocation) || !isAllowedUrl(originalUrl)) {
      return originalUrl;
    }

    const newFilters = buildFilters(currentLocation, originalUrl);
    return `${originalUrl}?${newFilters}`;
  },
};
